import { AsyncThunk, createAsyncThunk } from "@reduxjs/toolkit";

import { HandleErrorsService, ParsedError } from "../../services";
import {
  ApiResponseUpdate,
  AxiosParams,
  BaseFormValues,
  BaseRequest,
  BaseResponse,
} from "../types";
import { BaseService } from "../../services/base.service";

export const handleAsyncThunkError = (
  error: any,
  rejectWithValue: (value: ParsedError) => any
) => {
  const parsedError: ParsedError =
    error && error.message && error.typeName !== undefined
      ? error // already parsed
      : HandleErrorsService.parse(error); // fallback to parse raw error

  return rejectWithValue(parsedError);
};

interface CustomMethods<REQ extends BaseRequest, RES extends BaseResponse> {
  [key: string]: (item: REQ) => Promise<ApiResponseUpdate<RES>> | any;
}

interface createCRUDThunksParams<
  REQ extends BaseRequest,
  RES extends BaseResponse,
  FV extends BaseFormValues
> {
  service: BaseService<REQ, RES, FV>;
  resourceName: string;
  customMethods?: CustomMethods<REQ, RES>;
}

export interface CRUDThunks<REQ extends BaseRequest, RES extends BaseResponse> {
  fetchItems: AsyncThunk<RES[], AxiosParams | undefined, any>;
  getItemById: AsyncThunk<RES, string, any>;
  createItem: AsyncThunk<RES, REQ, any>;
  updateItem: AsyncThunk<RES, REQ, any>;
  deleteItem: AsyncThunk<string, string, any>;
  [key: string]: any; // for dynamic custom thunks
}

export const createCRUDThunks = <
  REQ extends BaseRequest,
  RES extends BaseResponse,
  FV extends BaseFormValues
>({
  service,
  resourceName,
  customMethods = {},
}: createCRUDThunksParams<REQ, RES, FV>) => {
  const debug = (...args: any[]) => {
    console.log(`🐞 DEBUG FROM THUNK [${resourceName}]:`, ...args);
  };

  const fetchItems = createAsyncThunk(
    `${resourceName}/fetchItems`,
    async (params: AxiosParams | undefined = {}, { rejectWithValue }) => {
      try {
        const data = await service.getAll(params);
        return data.data;
      } catch (error) {
        return handleAsyncThunkError(error, rejectWithValue);
      }
    }
  );

  const getItemById = createAsyncThunk(
    `${resourceName}/getItemById`,
    async (id: string, { rejectWithValue }) => {
      try {
        const data = await service.getById(id);
        return data;
      } catch (error) {
        return handleAsyncThunkError(error, rejectWithValue);
      }
    }
  );

  const createItem = createAsyncThunk(
    `${resourceName}/createItem`,
    async (item: REQ, { rejectWithValue }): Promise<RES> => {
      try {
        const data = await service.create(item);
        return data.data;
      } catch (error) {
        return handleAsyncThunkError(error, rejectWithValue);
      }
    }
  );

  const updateItem = createAsyncThunk(
    `${resourceName}/updateItem`,
    async (item: REQ, { rejectWithValue }): Promise<RES> => {
      try {
        const data = customMethods.update
          ? await customMethods.update(item)
          : await service.update(item.id!, item);
        return data.data;
      } catch (error) {
        return handleAsyncThunkError(error, rejectWithValue);
      }
    }
  );

  const deleteItem = createAsyncThunk(
    `${resourceName}/deleteItem`,
    async (id: string, { rejectWithValue }) => {
      try {
        await service.delete(id);
        return id;
      } catch (error) {
        return handleAsyncThunkError(error, rejectWithValue);
      }
    }
  );

  // Registra otros métodos personalizados si existen
  const customThunks = Object.keys(customMethods).reduce((acc, methodName) => {
    acc[methodName] = createAsyncThunk(
      `${resourceName}/${methodName}`,
      async (item: REQ, { rejectWithValue }): Promise<RES> => {
        try {
          return await customMethods[methodName](item);
        } catch (error) {
          return handleAsyncThunkError(error, rejectWithValue);
        }
      }
    );
    return acc;
  }, {} as { [key: string]: any });

  return {
    fetchItems,
    getItemById,
    createItem,
    updateItem,
    deleteItem,
    ...customThunks,
  } as CRUDThunks<REQ, RES>;
};
